<script setup lang="ts">
import PurchaseTabSubscribe from './PurchaseTabSubscribe.vue'
import PurchaseTabOnce from './PurchaseTabOnce.vue'

import { usePurchaseState } from './lib/PurchaseState'
import type { Product } from '@/lib/types/Product'
import type { User } from '@/lib/types/User'
import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { WorkState } from '@/lib/types/WorkState'

const props = withDefaults(
  defineProps<{
    product: Product
    user: User
    initialWhen?: 'now' | 'next'
    loginLink?: string
    customerPortalLink?: string
    nextOrderLink?: string
    purchaseState: WorkState
    initialSellingPlan?: string
    initialQuantity?: number
    initialVariantId?: string
    plasticWasteSaving?: {
      sku: string
      plastic_waste_saved_g: number
    }
    showLink: boolean
  }>(),
  {
    initialWhen: 'now',
    loginLink: '#',
    customerPortalLink: '#',
    nextOrderLink: '#',
    initialQuantity: 1,
    showLink: true,
  },
)

const emit = defineEmits<{
  (e: 'interaction'): void
  (e: 'variantChange', variantId: string): void
  (e: 'purchase', purchaseData: PurchaseData): void
  (e: 'viewProduct', productId: string): void
}>()

const {
  selectedVariantId,
  selectedVariant,
  selectedSellingPlanId,
  isSubscriptionProduct,
  subscribeAndSave,
  price,
  subscribePrice,
  pricePer,
  savePercent,
  quantity,
  when,
  subscriptionToThisProduct,
  emitPurchaseEvent,
  url,
  nextOrderDate,
} = usePurchaseState(props, emit)
</script>

<template>
  <form class="Purchase Purchase--quick" @submit.prevent>
    <span v-if="subscribeAndSave" class="Purchase__Label">
      Subscribe &amp; Save {{ savePercent }}%
    </span>
    <div class="Stack Stack--small">
      <h2 class="Purchase__ProductTitle h3">
        {{ product.title }}
      </h2>
      <template v-if="isSubscriptionProduct">
        <PurchaseTabSubscribe
          :price="price"
          :price-per="pricePer"
          :compare-at-price="selectedVariant.compareAtPrice"
          :subscribe-price="subscribePrice"
          :save-percent="savePercent"
          :allow-subscribe="isSubscriptionProduct"
          :quantity="quantity"
          :when="when"
          :selected-selling-plan-id="selectedSellingPlanId"
          :selling-plan-allocations="selectedVariant.sellingPlanAllocations"
          :is-logged-in="user.isLoggedIn"
          :user-load-state="user.loadState"
          :next-order-date="nextOrderDate"
          :login-link="loginLink"
          :subscription-to-this-product="subscriptionToThisProduct"
          :purchase-state="purchaseState"
          :customer-portal-link="customerPortalLink"
          :next-order-link="nextOrderLink"
          :in-tab="false"
          :show-inline-variant-selector="true"
          :variants="product.variants"
          :variant-option-label="product.variantOptionLabel"
          :selected-variant-id="selectedVariantId"
          :available="selectedVariant.available"
          :url="url"
          :plastic-waste-saving="plasticWasteSaving"
          :show-link="showLink"
          :related-product-reviews="product.relatedProductReviews"
          @quantity:changed="quantity = $event"
          @when:changed="when = $event"
          @purchase="emitPurchaseEvent"
          @variant:changed="selectedVariantId = $event"
          @selected-selling-plan:changed="selectedSellingPlanId = $event"
          @view-product="$emit('viewProduct', product.id)"
        />
      </template>
      <template v-else>
        <PurchaseTabOnce
          :price="price"
          :price-per="pricePer"
          :compare-at-price="selectedVariant.compareAtPrice"
          :subscribe-price="subscribePrice"
          :save-percent="savePercent"
          :allow-subscribe="isSubscriptionProduct"
          :quantity="quantity"
          :when="when"
          :selected-selling-plan-id="selectedSellingPlanId"
          :selling-plan-allocations="selectedVariant.sellingPlanAllocations"
          :is-logged-in="user.isLoggedIn"
          :user-load-state="user.loadState"
          :next-order-date="nextOrderDate"
          :login-link="loginLink"
          :subscription-to-this-product="subscriptionToThisProduct"
          :purchase-state="purchaseState"
          :customer-portal-link="customerPortalLink"
          :in-tab="false"
          :show-inline-variant-selector="true"
          :variants="product.variants"
          :variant-option-label="product.variantOptionLabel"
          :selected-variant-id="selectedVariantId"
          :next-order-link="nextOrderLink"
          :available="selectedVariant.available"
          :url="url"
          :plastic-waste-saving="plasticWasteSaving"
          :show-link="showLink"
          :related-product-reviews="product.relatedProductReviews"
          @quantity:changed="quantity = $event"
          @when:changed="when = $event"
          @purchase="emitPurchaseEvent"
          @variant:changed="selectedVariantId = $event"
          @selected-selling-plan:changed="selectedSellingPlanId = $event"
          @view-product="emit('viewProduct', product.id)"
        />
      </template>
    </div>
  </form>
</template>

<style>
.Purchase--quick {
  max-width: 560px;
  margin: 0 auto;
}

@media (--mq-phablet-and-up) {
  .Purchase--quick {
    margin: 0;
  }
}

.Purchase__ProductTitle {
  padding-right: 47px;
}

.Purchase__Label {
  display: inline-block;
  background-color: var(--color-forest-100);
  border-radius: 2px;
  color: var(--color-white);
  font-family: var(--font-family-rund);
  font-weight: bold;
  font-size: 13px;
  padding: 2px 6px;
  margin-bottom: 10px;
}

@media (--mq-phablet-and-up) {
  .Purchase__Label {
    font-size: 15px;
  }
}
</style>
